import {
  Card,
  Col,
  Heading2,
  Row,
  Stack,
  VisuallyHidden,
} from "@ovotech/element";
import React, { PropsWithChildren, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Charger, ProductUrls } from "_core/types";
import { useStore } from "_core/store";
import Mixpanel from "_core/analytics/Mixpanel";
import { AnalyticsEvents } from "_core/analytics/AnalyticsEvents";
import { pagesForCharger } from "_core/utils/charger";
import { StepProgressBar } from "_core/components/StepProgressBar";

interface Props {
  header?: boolean;
  displaySteps?: boolean;
  title?: string | JSX.Element;
  subtitle?: string | JSX.Element;
  children: PropsWithChildren<any>;
  card?: boolean;
  url: ProductUrls;
  sendViewPageEvent?: boolean;
  fullWidth?: boolean;
}

const Container = styled.main`
  flex-grow: 1;
`;

const StyledCol = styled(Col)`
  max-width: 100%;
  margin: 0 auto;
  ${({ theme: { customMediaQuery } }) => `
  ${customMediaQuery["small-and-up"]} {
    max-width: 70%;
  }
`}
`;

const HeaderNav = styled.div`
  display: flex;
`;

export const Page = ({
  displaySteps,
  title,
  subtitle,
  children,
  url,
  header = true,
  card = false,
  fullWidth = false,
  sendViewPageEvent = true,
}: Props) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { charger, cableSetup, cableLength, baseColour } = useStore();
  const pages = pagesForCharger(charger);
  const steps = pages.length;
  const current = url && pages.indexOf(url);

  const chargerCustomisationProps = useMemo(() => {
    if (charger === Charger.INDRA && cableSetup !== null) {
      return { cableSetup };
    }
    if (
      charger === Charger.HYPERVOLT &&
      cableLength !== null &&
      baseColour !== null
    ) {
      return { cableLength, baseColour };
    }
    return {};
  }, [charger, cableSetup, cableLength, baseColour]);

  useEffect(() => {
    if (url && sendViewPageEvent) {
      Mixpanel.track(AnalyticsEvents.VIEWEDPAGE, {
        pageName: url,
        charger,
        ...chargerCustomisationProps,
      });
    }
  }, [sendViewPageEvent, url, charger, chargerCustomisationProps]);

  return (
    <>
      <Container>
        <Row>
          <StyledCol
            style={{ maxWidth: fullWidth ? "100%" : undefined }}
            id="main"
          >
            <Stack spaceBetween={10}>
              {header && (
                <HeaderNav>
                  {displaySteps && (
                    <>
                      <StepProgressBar
                        currentStep={current + 1}
                        totalSteps={steps}
                      />
                    </>
                  )}
                </HeaderNav>
              )}

              {title && (
                <>
                  {title}
                  {subtitle && <Heading2>{subtitle}</Heading2>}
                </>
              )}

              {card ? <Card>{children}</Card> : children}
            </Stack>
          </StyledCol>
        </Row>
        <VisuallyHidden>
          <div
            id="live-region"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          />
        </VisuallyHidden>
      </Container>
    </>
  );
};
